// Replaces DeliveryOrderExportRetryDialog.tsx (ticket 09/10, see
// .scratch/original-document-retrofit). Single "Order details" modal showing
// this order's current (live) data, with two ways to change any of it:
// edit fields directly right here, or upload the original document and let
// OCR extract/compare/merge (UploadFlowFullScreen). Every field is editable
// either way — driver_name/car_plates/trailer_plates included, now that gps
// (mbugno/delivery-order-api) persists them too, not just the original
// 4-field merge scope. Picking a file hands off immediately to
// UploadFlowFullScreen (mirrors "Add transport order": go full-screen right
// away, show processing steps there, not in the modal).
//
// Save is the only save/export action here: it persists fields (+ the
// source file URL, when a document was uploaded) via Mike's
// update_delivery_order ad-hoc (ticket 01) — whatever export decision
// follows from that is gps's call, not the frontend's. Fields and the export
// badge are read from show_latest_delivery_order on open, and from
// update_delivery_order's own response after every Save/merge — both return
// the identical shape (id/fields/export_status/export_id/exported_at), so
// Save refreshes the badge instead of leaving it stale from the last load.
// Deliberately never the XMPP-pushed exportStatus/show_delivery_order_export,
// which could show a misleading "Exported" for exports the frontend never
// gated (see .scratch/original-document-retrofit).

import { useContext, useEffect, useRef, useState } from "react"
import type { Element } from "@xmpp/xml"
import { toast } from "sonner"
import { Loader2, Upload } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import UploadFlowFullScreen from "@/components/maps/orderDetails/UploadFlowFullScreen"
import { ResolutionState } from "@/components/maps/orderDetails/compareFields"
import { useOriginalDocumentUpload } from "@/components/maps/orderDetails/useOriginalDocumentUpload"
import { printedRef } from "@/lib/printedRef"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"
import { getIntlLocale } from "@/lib/i18n/format"

export type FormFields = {
  driverName: string
  carPlates: string
  trailerPlates: string
  price: string
  priceCurrency: string
  referenceNumber: string
  orderNumber: string
  nip: string
  customerName: string
  coordinatorName: string
  emptyKilometers: string
  loadedKilometers: string
  // "true" | "false" | "" — the gps form spelling of the yes/no.
  palletExchange: string
  // Bare °C numbers as text, "" when unset.
  temperatureMin: string
  temperatureMax: string
}

// Labels are translation keys; callers translate them when they render.
export const PALLET_EXCHANGE_OPTIONS = [
  { value: "", label: "orders.orderDetails.palletExchange.noData" },
  { value: "true", label: "orders.orderDetails.palletExchange.yes" },
  { value: "false", label: "orders.orderDetails.palletExchange.no" },
] as const

export const formatPalletExchange = (value: string): string => {
  const option = PALLET_EXCHANGE_OPTIONS.find(
    (option) => option.value === value,
  )
  return option ? i18n.t(option.label) : ""
}

export type LatestDeliveryOrder = {
  id: string
  fields: FormFields
  // Raw gps export_status values: "pending" | "failed" | "success" | ""
  // (never attempted).
  exportStatus: string
  exportId: string
  exportedAt: string
}

// Single source of truth for classifying the raw gps export_status string —
// shared with DeliveryOrderExportStatusCard so both places react to the same
// set of states instead of two independently-maintained if/else chains.
export type ExportStatusClass = "success" | "error" | "pending" | "none"

export const classifyExportStatus = (status: string): ExportStatusClass =>
  status === "success"
    ? "success"
    : status === "failed"
      ? "error"
      : status === "pending"
        ? "pending"
        : "none"

// Single source of truth for the tooltip text too — shared by
// DeliveryOrderExportStatusCard and DriverOverviewBar's OrderStatusLink so
// the same order reads the same way wherever the dispatcher opens it from.
// i18n.t at call time: both callers re-render on a language switch.
export const exportStatusTooltip = (
  hasOrder: boolean,
  status: ExportStatusClass,
  exportId?: string,
): string =>
  !hasOrder
    ? i18n.t("orders.orderDetails.exportTooltip.noOrder")
    : status === "success"
      ? exportId
        ? i18n.t("orders.orderDetails.exportTooltip.exportedWithId", {
            id: exportId,
          })
        : i18n.t("orders.orderDetails.exportTooltip.exported")
      : status === "error"
        ? i18n.t("orders.orderDetails.exportTooltip.failed")
        : status === "pending"
          ? i18n.t("orders.orderDetails.exportTooltip.inProgress")
          : i18n.t("orders.orderDetails.exportTooltip.notExported")

// Labels are translation keys, translated when the badge renders.
const EXPORT_BADGES = {
  success: {
    variant: "positive",
    label: "orders.orderDetails.exportBadge.exported",
  },
  error: { variant: "error", label: "orders.orderDetails.exportBadge.failed" },
  pending: {
    variant: "neutral",
    label: "orders.orderDetails.exportBadge.pending",
  },
  none: {
    variant: "neutral",
    label: "orders.orderDetails.exportBadge.notExported",
  },
} as const satisfies Record<
  ExportStatusClass,
  { variant: "positive" | "error" | "neutral"; label: string }
>

// Response shape shared by show_latest_delivery_order AND update_delivery_order
// (gps mbugno/delivery-order-api) — both return the same field set, so this
// parses either. Note the server key is customer_nip, not nip — a mismatch
// with the rest of gps (export xml builder reads metadata["nip"]), flagged
// on ticket 01.
export const parseLatestDeliveryOrder = (
  result: Element,
): LatestDeliveryOrder => {
  const form = result.getChild("command")?.getChild("x", "jabber:x:data")
  const value = (varName: string) =>
    form
      ?.getChildren("field")
      .find((f: Element) => f.getAttr("var") === varName)
      ?.getChildText("value") ?? ""
  const currency = value("currency")

  return {
    id: value("id"),
    fields: {
      driverName: value("driver_name"),
      carPlates: value("car_plates"),
      trailerPlates: value("trailer_plates"),
      price: value("price"),
      priceCurrency: currency ? currency.toUpperCase() : "EUR",
      referenceNumber: value("reference_number"),
      orderNumber: value("order_number"),
      nip: value("customer_nip"),
      customerName: value("customer_name"),
      coordinatorName: value("coordinator_name"),
      emptyKilometers: value("empty_kilometers"),
      loadedKilometers: value("loaded_kilometers"),
      palletExchange: value("pallet_exchange"),
      temperatureMin: value("temperature_min"),
      temperatureMax: value("temperature_max"),
    },
    exportStatus: value("export_status"),
    exportId: value("export_id"),
    exportedAt: value("exported_at"),
  }
}

type FieldLabelKey =
  | "orders.orderDetails.fields.customerName"
  | "orders.orderDetails.fields.nip"
  | "orders.orderDetails.fields.coordinatorName"
  | "orders.orderDetails.fields.orderNumber"
  | "orders.orderDetails.fields.referenceNumber"
  | "orders.orderDetails.fields.price"
  | "orders.orderDetails.fields.emptyKilometers"
  | "orders.orderDetails.fields.loadedKilometers"
  | "orders.orderDetails.fields.driverName"
  | "orders.orderDetails.fields.carPlates"
  | "orders.orderDetails.fields.trailerPlates"
  | "orders.orderDetails.fields.palletExchange"
  | "orders.orderDetails.fields.temperatureMin"
  | "orders.orderDetails.fields.temperatureMax"

// Shared field defs for both the read-only preview and the edit form —
// `key` picks the FormFields slot the Edit form binds to (price is edited as
// a bare number, currency is implicitly EUR everywhere in this flow).
// `kind` picks the editor: a plain text Input unless told otherwise.
// `label` is a translation key, translated when the row renders.
const FIELD_DEFS: {
  key: keyof FormFields
  label: FieldLabelKey
  get: (f: FormFields) => string
  kind?: "text" | "yesno" | "number"
}[] = [
  {
    key: "customerName",
    label: "orders.orderDetails.fields.customerName",
    get: (f) => f.customerName,
  },
  { key: "nip", label: "orders.orderDetails.fields.nip", get: (f) => f.nip },
  {
    key: "coordinatorName",
    label: "orders.orderDetails.fields.coordinatorName",
    get: (f) => f.coordinatorName,
  },
  {
    key: "orderNumber",
    label: "orders.orderDetails.fields.orderNumber",
    get: (f) => f.orderNumber,
  },
  {
    key: "referenceNumber",
    label: "orders.orderDetails.fields.referenceNumber",
    get: (f) => printedRef(f.referenceNumber, f.orderNumber) ?? "",
  },
  {
    key: "price",
    label: "orders.orderDetails.fields.price",
    get: (f) => (f.price ? `${f.price} ${f.priceCurrency}` : ""),
  },
  {
    key: "emptyKilometers",
    label: "orders.orderDetails.fields.emptyKilometers",
    get: (f) => f.emptyKilometers,
  },
  {
    key: "loadedKilometers",
    label: "orders.orderDetails.fields.loadedKilometers",
    get: (f) => f.loadedKilometers,
  },
  {
    key: "driverName",
    label: "orders.orderDetails.fields.driverName",
    get: (f) => f.driverName,
  },
  {
    key: "carPlates",
    label: "orders.orderDetails.fields.carPlates",
    get: (f) => f.carPlates,
  },
  {
    key: "trailerPlates",
    label: "orders.orderDetails.fields.trailerPlates",
    get: (f) => f.trailerPlates,
  },
  {
    key: "palletExchange",
    label: "orders.orderDetails.fields.palletExchange",
    get: (f) =>
      f.palletExchange ? formatPalletExchange(f.palletExchange) : "",
    kind: "yesno",
  },
  {
    key: "temperatureMin",
    label: "orders.orderDetails.fields.temperatureMin",
    get: (f) => f.temperatureMin,
    kind: "number",
  },
  {
    key: "temperatureMax",
    label: "orders.orderDetails.fields.temperatureMax",
    get: (f) => f.temperatureMax,
    kind: "number",
  },
]

// Shared by both save paths (manual-edit and upload+OCR+merge) — the only
// rename gps's ad-hoc needs is nip -> customer_nip (see parseLatestDeliveryOrder).
// A key left undefined (the upload+merge path knows nothing about pallets or
// temperature) is omitted from the ad-hoc, so gps leaves that value alone.
const toUpdatePayload = (f: Partial<Omit<FormFields, "priceCurrency">>) => ({
  referenceNumber: f.referenceNumber,
  orderNumber: f.orderNumber,
  price: f.price,
  customerNip: f.nip,
  customerName: f.customerName,
  driverName: f.driverName,
  carPlates: f.carPlates,
  trailerPlates: f.trailerPlates,
  coordinatorName: f.coordinatorName,
  emptyKilometers: f.emptyKilometers,
  loadedKilometers: f.loadedKilometers,
  palletExchange: f.palletExchange,
  temperatureMin: f.temperatureMin,
  temperatureMax: f.temperatureMax,
})

const OrderDetailsDialog = ({
  driverJid,
  deliveryOrderId,
  isOpen,
  onClose,
  seedFields,
  onSaved,
}: {
  driverJid: string
  deliveryOrderId: string
  isOpen: boolean
  onClose: () => void
  // When provided (report entry point): render straight from these fields
  // instead of fetching via show_latest_delivery_order, which only ever
  // returns the driver's NEWEST order — useless for a report row from
  // days/weeks ago. No export status comes with the seed, so the export
  // badge stays hidden (see below) rather than guess/show a possibly-wrong
  // "Not exported".
  seedFields?: FormFields
  // Fires after every successful save (manual-edit or upload+merge) with the
  // server's fresh response. Callers seeding this dialog from their own
  // list/table data (no shared store) need this to patch their local copy.
  onSaved?: (data: LatestDeliveryOrder) => void
}) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)

  const [loading, setLoading] = useState(false)
  // seedFields is only ever provided by a caller that mounts a fresh
  // instance per row (OrdersTable: `{detailsOrder && <OrderDetailsDialog
  // .../>}`) — never updates on an existing instance — so a lazy initializer
  // is enough; no effect needed to sync it in.
  const [fields, setFields] = useState<FormFields | null>(
    () => seedFields ?? null,
  )
  const [exportId, setExportId] = useState<string>("")
  const [exportStatus, setExportStatus] = useState<string>("")
  const [exportedAt, setExportedAt] = useState<string>("")
  const [loadError, setLoadError] = useState(false)
  // True once export status has actually been fetched/returned by the
  // server (initial load, or any Save/merge response) — distinct from
  // `fields` being set, which also happens for a seeded report row that
  // carries no export status at all. Gates the badge so a seeded dialog
  // never shows a guessed "Not exported".
  const [hasExportInfo, setHasExportInfo] = useState(false)

  // Manual-edit path: edit fields directly, no document involved. Parallel
  // to (and mutually exclusive with) the upload -> OCR -> compare path below.
  const [editing, setEditing] = useState(false)
  const [draftFields, setDraftFields] = useState<FormFields | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const firstFieldRef = useRef<HTMLInputElement | null>(null)

  const isDirty =
    editing &&
    draftFields !== null &&
    fields !== null &&
    JSON.stringify(draftFields) !== JSON.stringify(fields)

  useEffect(() => {
    if (editing) firstFieldRef.current?.focus()
  }, [editing])

  // Both show_latest_delivery_order and update_delivery_order return the
  // identical shape (id/fields/export_status/export_id/exported_at) — this
  // is the one place that reflects either response into local state, so the
  // badge/fields never go stale after a load, merge-save, or manual-edit save.
  const applyLatestOrder = (data: LatestDeliveryOrder) => {
    setFields(data.fields)
    setExportId(data.exportId)
    setExportStatus(data.exportStatus)
    setExportedAt(data.exportedAt)
    setHasExportInfo(true)
  }

  const {
    fileInputRef,
    uploading,
    error: uploadError,
    result: uploadResult,
    handleFile,
    triggerFilePick,
    reset: resetUpload,
  } = useOriginalDocumentUpload(driverJid)

  // Declared here rather than next to its use below the early return: the
  // reload effect needs it in its dependency array, which is evaluated
  // during render.
  const inUploadFlow =
    uploading || Boolean(uploadError) || Boolean(uploadResult)

  // Reloads every time the dialog opens (not just on first mount) — the
  // dialog stays mounted across open/close within a session (only `isOpen`
  // toggles), so without this the badge/fields would show whatever was
  // fetched the first time, stale on every reopen.
  //
  // Skipped while editing or in the upload flow: `client` is a dependency,
  // and an XMPP reconnect swaps that identity at any moment — mid-edit that
  // means `loading` hides the form under the user and the response then
  // overwrites what they typed. Both flags are dependencies too, so leaving
  // either state runs the reload that was skipped, with no stale data left
  // behind.
  useEffect(() => {
    if (!isOpen || seedFields) return
    if (!client || !deliveryOrderId) return
    if (editing || inUploadFlow) return

    let cancelled = false
    setLoading(true)
    setLoadError(false)

    AdHocCommands.showLatestDeliveryOrder(client, driverJid)
      .then((result) => {
        if (!cancelled) applyLatestOrder(parseLatestDeliveryOrder(result))
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    // Without this an out-of-order response from a superseded load (two
    // `client` swaps in quick succession) could overwrite the newer one.
    return () => {
      cancelled = true
    }
  }, [
    client,
    deliveryOrderId,
    driverJid,
    isOpen,
    seedFields,
    editing,
    inUploadFlow,
  ])

  // price_netto_eur resolution values are formatted for display ("2500.00
  // EUR", see compareFields.ts formatPrice) — Mike's update_delivery_order
  // wants the bare number, currency is implicitly EUR everywhere in this
  // flow (map scope: price_netto_eur, no other currency in play).
  const bareNumber = (formatted: string) => formatted.trim().split(/\s+/)[0]

  const handleMergeSaved = async (resolution: ResolutionState) => {
    if (!client || !fields)
      throw new Error(t("orders.orderDetails.notConnected"))

    const result = await AdHocCommands.updateDeliveryOrder(
      client,
      deliveryOrderId,
      {
        ...toUpdatePayload({
          referenceNumber: resolution.ref_number.value,
          orderNumber: resolution.order_number.value,
          price: bareNumber(resolution.price_netto_eur.value),
          nip: resolution.nip.value,
          customerName: resolution.customer_name.value,
          driverName: resolution.driver_name.value,
          carPlates: resolution.car_plates.value,
          trailerPlates: resolution.trailer_plates.value,
          coordinatorName: resolution.coordinator_name.value,
          emptyKilometers: resolution.empty_kilometers.value,
          loadedKilometers: resolution.loaded_kilometers.value,
        }),
        sourceFileUrl: uploadResult?.fileUrl,
      },
    )

    const parsed = parseLatestDeliveryOrder(result)
    applyLatestOrder(parsed)
    onSaved?.(parsed)
    // Close straight through to the underlying table instead of dropping
    // back into the "Order details" modal — that intermediate step was
    // redundant right after a merge save.
    handleClose()
    toast.success(t("orders.orderDetails.saved"))
  }

  const startEditing = () => {
    if (!fields) return
    setDraftFields(fields)
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setDraftFields(null)
  }

  const updateDraftField = (key: keyof FormFields, value: string) => {
    setDraftFields((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  const saveEditing = async () => {
    if (!client || !draftFields) return
    setSavingEdit(true)
    try {
      const result = await AdHocCommands.updateDeliveryOrder(
        client,
        deliveryOrderId,
        toUpdatePayload(draftFields),
      )
      const parsed = parseLatestDeliveryOrder(result)
      applyLatestOrder(parsed)
      onSaved?.(parsed)
      setEditing(false)
      setDraftFields(null)
      toast.success(t("orders.orderDetails.saved"))
    } catch (err: any) {
      toast.error(err?.message ?? t("orders.orderDetails.saveFailed"))
    } finally {
      setSavingEdit(false)
    }
  }

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (file && fields) handleFile(file, fields)
  }

  const handleClose = () => {
    resetUpload()
    cancelEditing()
    onClose()
  }

  // Routes every close trigger (X button, Escape, outside click — all funnel
  // through Dialog's onOpenChange) through one dirty check, so an unsaved
  // edit is never silently discarded.
  const requestClose = () => {
    if (savingEdit) return
    if (isDirty) {
      setConfirmDiscard(true)
      return
    }
    handleClose()
  }

  const confirmDiscardAndClose = () => {
    setConfirmDiscard(false)
    handleClose()
  }

  if (inUploadFlow) {
    return (
      <UploadFlowFullScreen
        fileInputRef={fileInputRef}
        onFileInputChange={onFileInputChange}
        uploading={uploading}
        error={uploadError}
        result={uploadResult}
        onRetryFile={triggerFilePick}
        onDiscard={resetUpload}
        onSaved={handleMergeSaved}
      />
    )
  }

  const exportBadge = EXPORT_BADGES[classifyExportStatus(exportStatus)]

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && requestClose()}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {t("orders.orderDetails.title")}
              {fields && hasExportInfo && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Badge variant={exportBadge.variant}>
                          {t(exportBadge.label)}
                        </Badge>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {exportId && (
                        <div>
                          {t("orders.orderDetails.exportId", { id: exportId })}
                        </div>
                      )}
                      {exportedAt && (
                        <div>
                          {t("orders.orderDetails.lastSent", {
                            date: new Date(exportedAt).toLocaleString(
                              getIntlLocale(),
                            ),
                          })}
                        </div>
                      )}
                      {!exportId &&
                        !exportedAt &&
                        t("orders.orderDetails.noExportAttempted")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </DialogTitle>
            <DialogDescription>
              {t("orders.orderDetails.description")}
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {loadError && (
            <p className="text-sm text-destructive">
              {t("orders.orderDetails.loadFailed")}
            </p>
          )}

          {fields && !loading && (
            <div className="grid gap-4 py-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  {t("orders.orderDetails.orderData")}
                </span>
                {!editing && (
                  <Button size="sm" variant="outline" onClick={startEditing}>
                    {t("common.edit")}
                  </Button>
                )}
              </div>

              <div className="flex flex-col divide-y rounded-md border">
                {FIELD_DEFS.map(({ key, label, get, kind }, index) => (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span className="shrink-0 text-muted-foreground">
                      {t(label)}
                    </span>
                    {editing && draftFields ? (
                      kind === "yesno" ? (
                        <Select
                          value={draftFields[key]}
                          onValueChange={(value) =>
                            updateDraftField(
                              key,
                              value === "unset" ? "" : value,
                            )
                          }
                          disabled={savingEdit}
                        >
                          <SelectTrigger
                            className="h-7 max-w-[220px]"
                            data-testid={`order-field-${key}`}
                          >
                            <SelectValue
                              placeholder={t(
                                "orders.orderDetails.palletExchange.noData",
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {PALLET_EXCHANGE_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value || "unset"}
                                value={option.value || "unset"}
                              >
                                {t(option.label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          ref={index === 0 ? firstFieldRef : undefined}
                          type={kind === "number" ? "number" : "text"}
                          step={kind === "number" ? "0.5" : undefined}
                          value={draftFields[key]}
                          onChange={(e) =>
                            updateDraftField(key, e.target.value)
                          }
                          disabled={savingEdit}
                          className="h-7 max-w-[220px] text-right"
                        />
                      )
                    ) : (
                      <span className="font-medium">{get(fields) || "—"}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t pt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={onFileInputChange}
            />

            {editing ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button disabled>
                        <Upload className="mr-2 h-4 w-4" />
                        {t("orders.orderDetails.uploadOriginal")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("orders.orderDetails.finishEditingFirst")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button onClick={triggerFilePick} disabled={uploading || !fields}>
                <Upload className="mr-2 h-4 w-4" />
                {t("orders.orderDetails.uploadOriginal")}
              </Button>
            )}

            {editing && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={cancelEditing}
                  disabled={savingEdit}
                >
                  {t("common.cancel")}
                </Button>
                <Button onClick={saveEditing} disabled={savingEdit}>
                  {savingEdit && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("common.save")}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={(open) => !open && setConfirmDiscard(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("orders.orderDetails.discard.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("orders.orderDetails.discard.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("orders.orderDetails.discard.keepEditing")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardAndClose}>
              {t("orders.orderDetails.discard.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default OrderDetailsDialog
